import React from 'react';

import './Avatar.css';

export type AvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';
export type AvatarVariant = 'neutral' | 'primary' | 'muted';

export interface AvatarProps {
  /** Image source URL */
  src?: string;
  /** Alt text for image */
  alt?: string;
  /** Name for generating initials (used when no src provided) */
  name?: string;
  /** Size of the avatar */
  size?: AvatarSize;
  /** Color variant */
  variant?: AvatarVariant;
  /** Additional CSS class */
  className?: string;
  /** Aria label for accessibility */
  ariaLabel?: string;
}

/**
 * Get initials from a name string
 * "John Doe" -> "JD"
 * "John" -> "J"
 * "John Michael Doe" -> "JD" (first and last)
 */
function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

export const Avatar = React.memo(function Avatar({
  src,
  alt,
  name,
  size = 'md',
  variant = 'neutral',
  className = '',
  ariaLabel,
}: AvatarProps) {
  const [imageError, setImageError] = React.useState(false);

  const showImage = src && !imageError;
  const initials = name ? getInitials(name) : '';

  const handleImageError = () => {
    setImageError(true);
  };

  // Reset error state when src changes
  React.useEffect(() => {
    setImageError(false);
  }, [src]);

  return (
    <div
      className={`avatar ${className}`.trim()}
      data-size={size}
      data-variant={variant}
      aria-label={ariaLabel || alt || name}
      role="img"
    >
      {showImage ? (
        <img
          src={src}
          alt={alt || name || 'Avatar'}
          className="avatar__image"
          onError={handleImageError}
        />
      ) : (
        <span className="avatar__initials">{initials}</span>
      )}
    </div>
  );
});

// Avatar Group for displaying multiple avatars stacked
export interface AvatarGroupProps {
  /** Array of avatar props */
  avatars: AvatarProps[];
  /** Maximum number of avatars to show before +N indicator */
  max?: number;
  /** Size of avatars in the group */
  size?: AvatarSize;
  /** Additional CSS class */
  className?: string;
}

export const AvatarGroup = React.memo(function AvatarGroup({
  avatars,
  max = 4,
  size = 'sm',
  className = '',
}: AvatarGroupProps) {
  const visibleAvatars = avatars.slice(0, max);
  const remainingCount = avatars.length - max;

  return (
    <div className={`avatar-group ${className}`.trim()}>
      {visibleAvatars.map((avatarProps, index) => (
        <Avatar key={index} {...avatarProps} size={size} />
      ))}
      {remainingCount > 0 && (
        <div className="avatar avatar-group__overflow" data-size={size} data-variant="muted">
          <span className="avatar__initials">+{remainingCount}</span>
        </div>
      )}
    </div>
  );
});
